import React, { useCallback, useEffect, useState } from "react";

import Button from "src/components/ui/Button";
import Dropdown from "src/components/ui/Dropdown";
import SearchInput, { SearchInputOption } from "src/components/ui/SearchInput";

import productApis from "src/api/product";
import tenantUserApis from "src/api/tenantUser";
import { Product } from "src/types/product.types";

interface InventoryTransactionsFiltersProps {
  selectedFilters: any;
  handleReset: () => void;
  onFilterApply: (filter: any) => void;
}

const InventoryTransactionsFilters: React.FC<
  InventoryTransactionsFiltersProps
> = ({ selectedFilters = {}, handleReset, onFilterApply }) => {
  const [filters, setFilters] = useState({
    referenceType: "",
    productId: "",
    productName: "",
    createdBy: "",
    ...selectedFilters,
  });

  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const filterTypes = [
    { label: "Adjustment", value: "adjustment" },
    { label: "Purchase", value: "purchase" },
    { label: "Sale", value: "sale" },
  ];

  // Load users once
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const userRes = await tenantUserApis.getTenantUserList({
        limit: 1000,
      });

      if (userRes?.data) setUsers(userRes?.data || []);
    } catch (error) {
      console.error("Error loading filter data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Trigger once when popover opens
  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubmit = () => {
    const appliedFilters: any = { ...filters };

    if (!filters.productId) {
      delete appliedFilters.productName;
    }

    const selectedUser = users.find((u) => u._id === filters.createdBy);
    if (selectedUser) {
      appliedFilters.createdByName = `${selectedUser.firstName} (${selectedUser.role.name})`;
    } else {
      delete appliedFilters.createdByName;
    }
    onFilterApply(appliedFilters);
  };

  return (
    <div className="space-y-4">
      {loading && (
        <p className="text-xs text-gray-400 mb-2">Loading filters...</p>
      )}

      <Dropdown
        label="Transaction Type"
        options={filterTypes}
        value={filters.referenceType}
        onChange={(value) =>
          setFilters((prev: any) => ({ ...prev, referenceType: value }))
        }
        fullWidth
      />

      <SearchInput<Product>
        label="Product"
        placeholder="Search by name, SKU, HSN, or product ID..."
        value={filters.productName}
        onSearch={async (query) => {
          const res = await productApis.getProductList({
            page: 1,
            limit: 10,
            search: query,
          });
          const list: Product[] = res?.data?.data || res?.data || [];
          return list.map((p) => ({
            value: p._id || "",
            label: `${p.name} (${p.productId})`,
            data: p,
          }));
        }}
        onSelect={(option: SearchInputOption<Product>) =>
          setFilters((prev: any) => ({
            ...prev,
            productId: option.value,
            productName: option.data.name,
          }))
        }
        onClear={() =>
          setFilters((prev: any) => ({ ...prev, productId: "", productName: "" }))
        }
        renderOption={(option) => (
          <div>
            <div className="font-medium text-gray-800">
              {option.data.name}
            </div>
            <div className="text-xs text-gray-500">
              SKU: {option.data.sku || "-"} · HSN:{" "}
              {option.data.hsnCode || "-"} · ID: {option.data.productId}
            </div>
          </div>
        )}
      />

      <Dropdown
        label="Created By"
        options={users.map((u) => ({
          label: `${u.firstName} (${u.role.name})`,
          value: u._id,
        }))}
        value={filters.createdBy}
        onChange={(value) =>
          setFilters((prev: any) => ({ ...prev, createdBy: value }))
        }
        fullWidth
      />

      <div className="mt-4 flex items-center justify-between gap-4">
        <Button variant="outline" onClick={handleReset} fullWidth>
          Reset
        </Button>
        <Button variant="primary" onClick={handleSubmit} fullWidth>
          Apply
        </Button>
      </div>
    </div>
  );
};

export default InventoryTransactionsFilters;
